<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          Rule Properties
        </template>
        <template #actions v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')">
          <ul>
            <li class="p-narrow no-border">
              <div class="list-content">
                <a role="button"
                  @keypress.enter.prevent="{ editPanel(); showOptional() }"
                  @click="{ editPanel(); showOptional() }"
                  :class="{ disabled: editOtherPanels }"
                  v-if="!editCurrentPanel"
                  :tabindex="editOtherPanels ? -1 : 0"
                >
                  <font-awesome-icon :icon="['far', 'edit']" size="lg" title="Edit" />
                </a>
              </div>
            </li>
          </ul>
        </template>
      </EsHeader>
    </div>
    <div class="content">
      <es-form style="padding: 1.15rem 0rem">
        <template #es-form-header>
          <div class="header-container">
            <div class="title is-6">
              Data Validation Rules
            </div>
          </div>
        </template>
        <template #es-form>
          <table>
            <tbody>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Allowed Characters (Names)
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <div class="has-text-right is-pulled-right"
                      v-if="!editCurrentPanel"
                    >
                      {{ form.ALLOWED_CHARACTERS }}
                    </div>
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-danger' : errAllowedCharactersNames }"
                      type="text"
                      @keyup.prevent="validateAllowedCharactersNames"
                      v-model="form.ALLOWED_CHARACTERS"
                      aria-label="Allowed Characters (Names)"
                      v-else
                    >
                  </div>
                  <span v-if="errAllowedCharactersNames" class="help is-danger">
                    {{ msgAllowedCharactersNames }}
                  </span>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Maximum Records Per Submission
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel,
                        'is-danger' : errMaxRecPerSubmission }"
                      @keyup.prevent="validateMaxRecPerSubmission"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.MAX_RECORDS_PER_SUB"
                      aria-label="Maximum Records Per Submission"
                    >
                  </div>
                  <span v-if="errMaxRecPerSubmission" class="help is-danger">
                    {{ msgMaxRecPerSubmission }}
                  </span>
                </td>
              </tr>
              <tr>
                <td></td>
                <td
                  class="property has-text-grey"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Resident Location Submissions
                </td>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <div
                      class="buttons has-addons"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        v-for="(option, index) in enabledDisabled"
                        :key="index"
                        @click="form.RES_LOC_SUBMISSION = option.code"
                        :class="{ 'is-active': form.RES_LOC_SUBMISSION === option.code }">
                        {{ option.description }}
                      </button>
                    </div>
                    <input
                      v-else
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      type="text"
                      :value="form.RES_LOC_SUBMISSION === 'true' ? 'Enabled' : 'Disabled'"
                      :readonly="!editCurrentPanel"
                      aria-label="Resident Location Submissions"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Require Unique Filenames
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <div
                      class="buttons has-addons"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        v-for="(option, index) in enabledDisabled"
                        :key="index"
                        @click="form.REQ_UNIQUE_FILENAMES = option.code"
                        :class="{ 'is-active': form.REQ_UNIQUE_FILENAMES === option.code }">
                        {{ option.description }}
                      </button>
                    </div>
                    <input
                      v-else
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      type="text"
                      :value="form.REQ_UNIQUE_FILENAMES === 'true' ? 'Enabled' : 'Disabled'"
                      :readonly="!editCurrentPanel"
                      aria-label="Require Unique Filenames"
                    >
                  </div>
                </td>
              </tr>
              <!-- we removed property below from v2023 but we may add it back in the future -->
              <!-- <tr>
                <td></td>
                <td
                  class="property has-text-grey"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Look Up Validation Case Sensitivity
                </td>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <div
                      class="buttons has-addons"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        v-for="(option, index) in enabledDisabled"
                        :key="index"
                        @click="form.LOOKUP_VALID_CASE_SENSITIVITY = option.code"
                        :class="{'is-active': form.LOOKUP_VALID_CASE_SENSITIVITY === option.code }">
                        {{ option.description }}
                      </button>
                    </div>
                    <input
                      v-else
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      type="text"
                      :value="form.LOOKUP_VALID_CASE_SENSITIVITY === 'true'
                        ? 'Enabled'
                        : 'Disabled'"
                      :readonly="!editCurrentPanel"
                      aria-label="Look Up Validation Case Sensitivity"
                    >
                  </div>
                </td>
              </tr> -->
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Year(s) Allowed
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <div
                      class="buttons has-addons"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form big-button"
                        v-for="(option, index) in allowedYearsOptions"
                        :key="index"
                        @click="{ form.SCHOOL_YEARS_ALLOWED = option.description; hideOptional(); }"
                        :class="{ 'is-active': form.SCHOOL_YEARS_ALLOWED === option.description }"
                      >
                        {{ option.description }}
                      </button>
                    </div>
                    <div class="has-text-right is-pulled-right"
                      v-else
                    >
                      {{ form.SCHOOL_YEARS_ALLOWED }}
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Year Start Date (Month/Day)
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel,
                        'is-danger' : errSchoolYearStartDate }"
                      @keyup.prevent="validateMonthDay(form.YEAR_START_MONTH_DAY)"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.YEAR_START_MONTH_DAY"
                      aria-label="Year Start Date (Month/Day)"
                    >
                  </div>
                  <span v-if="errSchoolYearStartDate" class="help is-danger">
                    {{ msgSchoolYearStartDate }}
                  </span>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Prior Year Overlap Period Start Date (Month/Day)
                  <span v-if="editCurrentPanel && optionalShown">
                    (optional)
                  </span>
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel,
                        'is-danger' : errPriorYearStart }"
                      @keyup.prevent="validatePriorYearStart(form.PRIOR_YEAR_OVERLAP_START_DATE)"
                      :disabled="!editCurrentPanel"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.PRIOR_YEAR_OVERLAP_START_DATE"
                      aria-label="Prior Year Overlap Period Start Date (Month/Day)"
                    >
                  </div>
                  <span v-if="errPriorYearStart && !optionalShown" class="help is-danger">
                    {{ msgPriorYearStart }}
                  </span>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Prior Year Overlap Period End Date (Month/Day)
                  <span v-if="editCurrentPanel && optionalShown">
                    (optional)
                  </span>
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel,
                        'is-danger' : errPriorYearEnd  }"
                      @keyup.prevent="validatePriorYearEnd(form.PRIOR_YEAR_OVERLAP_END_DATE)"
                      :disabled="!editCurrentPanel"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.PRIOR_YEAR_OVERLAP_END_DATE"
                      aria-label="Prior Year Overlap Period End Date (Month/Day)"
                    >
                  </div>
                  <span v-if="errPriorYearEnd && !optionalShown" class="help is-danger">
                    {{ msgPriorYearEnd }}
                  </span>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Earliest Birth Year
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel, 'is-danger' : errBirthYear }"
                      @keypress="checkInput($event)"
                      @keyup.prevent="validateBirthYear(form.EARLIEST_BIRTH_YEAR)"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.EARLIEST_BIRTH_YEAR"
                      aria-label="Earliest Birth Year (optional)"
                    >
                  </div>
                  <span v-if="errBirthYear" class="help is-danger">
                    {{ msgBirthYear }}
                  </span>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Future Birth Years
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <div
                      class="buttons has-addons"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        v-for="(option, index) in enabledDisabled"
                        :key="index"
                        @click="form.FUTURE_BIRTH_YEARS = option.code"
                        :class="{ 'is-active': form.FUTURE_BIRTH_YEARS === option.code }">
                        {{ option.description }}
                      </button>
                    </div>
                    <input
                      v-else
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      type="text"
                      :value="form.FUTURE_BIRTH_YEARS === 'true' ? 'Enabled' : 'Disabled'"
                      :readonly="!editCurrentPanel"
                      aria-label="Future Birth Years"
                    >
                  </div>
                </td>
              </tr>
              <!-- We won’t have this property for v2023 but we will add it back -->
              <!--
              <tr>
                <td></td>
                <th class="property has-text-grey table-header-normal">
                  Enrollment Entry/Exit School Year(s) Allowed
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <div
                      class="buttons has-addons"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form big-button"
                        v-for="(option, index) in allowedYearsOptions"
                        :key="index"
                        @click="form.ENROLL_EE_SCHOOL_YEAR_ALLOWED = option.description"
                        :class="{
                          'is-active': form.ENROLL_EE_SCHOOL_YEAR_ALLOWED === option.description
                        }"
                      >
                        {{ option.description }}
                      </button>
                    </div>
                    <div class="has-text-right is-pulled-right"
                      v-else
                    >
                      {{ form.ENROLL_EE_SCHOOL_YEAR_ALLOWED }}
                    </div>
                  </div>
                </td>
              </tr>
              -->
            </tbody>
          </table>
        </template>
      </es-form>
      <es-form style="padding: 0rem 0rem 1.15rem 0rem">
        <template #es-form-header>
          <div class="header-container">
            <div class="title is-6">
              Errors
            </div>
          </div>
        </template>
        <template #es-form>
          <table>
            <tbody>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Maximum Number of Errors Allowed
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{
                        'is-static': !editCurrentPanel, 'is-danger' : errMaxNumberErrorsAllowed
                      }"
                      @keypress="checkInput($event)"
                      @keyup.prevent="validateMaxNumberErrorsAllowed(form.MAX_ERRORS_ALLOWED)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.MAX_ERRORS_ALLOWED"
                      aria-label="Maximum Number of Errors Allowed (optional)"
                    >
                  </div>
                   <span v-if="errMaxNumberErrorsAllowed" class="help is-danger">
                    {{ msgMaxNumberErrorsAllowed }}
                  </span>
                </td>
              </tr>
              <tr>
                <td></td>
                <td class="property has-text-grey">
                  Data Validation Handler
                </td>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                  <!-- we will use code in comment below when Fix Errors is ready -->
                    <!-- <div
                      class="buttons has-addons"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form big-button"
                        v-for="(option, index) in dataValidationHandlerOptions"
                        :key="index"
                        @click="form.DATA_VALIDATION_HANDLER = option.description"
                        :class="{
                          'is-active': form.DATA_VALIDATION_HANDLER === option.description
                        }"
                      >
                        {{ option.description }}
                      </button>
                    </div>
                    <input
                      v-else
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      type="text"
                      :value="form.DATA_VALIDATION_HANDLER"
                      :readonly="!editCurrentPanel"
                    > -->
                    <input
                      class="input is-static has-text-right is-pulled-right"
                      type="text"
                      :value="form.DATA_VALIDATION_HANDLER"
                      readonly
                      aria-label="Data Validation Handler"
                    >
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </es-form>
      <es-form style="padding: 0rem 0rem 1.15rem 0rem">
        <template #es-form-header>
          <div class="header-container">
            <div class="title is-6">
              Multiple Record
            </div>
          </div>
        </template>
        <template #es-form>
          <table>
            <tbody>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Asynchronous Assignment Limit
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.ASYNCH_ASSIGN_LIMIT"
                      aria-label="Asynchronous Assignment Limit"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Synchronous Assignment Limit
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.SYNCH_ASSIGN_LIMIT"
                      aria-label="Synchronous Assignment Limit"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Potential Candidate Result Limit - Assignment
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.CANDIDATE_LIMIT_ASSIGN"
                      aria-label="Potential Candidate Result Limit - Assignment"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Advanced Search Results Limit
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.RESULTS_LIMIT_ADVSRCH"
                      aria-label="Advanced Search Results Limit"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Asynchronous Advanced Search Limit
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.ASYNCH_SEARCH_ADV_LIMIT"
                      aria-label="Asynchronous Advanced Search Limit"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Synchronous Advanced Search Limit
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.SYNCH_SEARCH_ADV_LIMIT"
                      aria-label="Synchronous Advanced Search Limit"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Potential Candidate Result Limit - Advanced Search
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.CANDIDATE_LIMIT_ADVSRCH"
                      aria-label="Potential Candidate Result Limit - Advanced Search"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Asynchronous Search By ID Limit
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.ASYNCH_SEARCH_IDSRCH_LIMIT"
                      aria-label="Asynchronous Search By ID Limit"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Synchronous Search By ID Limit
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.SYNCH_SEARCH_IDSRCH_LIMIT"
                      aria-label="Synchronous Search By ID Limit"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Potential Candidate Result Limit - Search By ID
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.CANDIDATE_LIMIT_IDSRCH"
                      aria-label="Potential Candidate Result Limit - Search By ID"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Asynchronous Search By Person Limit
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.ASYNCH_SEARCH_PRSN_LIMIT"
                      aria-label="Asynchronous Search By Person Limit"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Search By Person Result Limit - Matching Engine
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.RESULTS_LIMIT_PRSNSRCH_MEA"
                      aria-label="Asynchronous Search By Person Limit"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                Search By Person Result Limit - SQL
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.RESULTS_LIMIT_PRSNSRCH_SQL"
                      aria-label="Asynchronous Search By Person Limit"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Synchronous Search By Person Limit
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.SYNCH_SEARCH_PRSN_LIMIT"
                      aria-label="Synchronous Search By Person Limit"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <td></td>
                <th
                  class="property has-text-grey top-row table-header-normal"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Potential Candidate Result Limit - Search By Person
                </th>
                <td class="top-row" :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      @keypress="checkInput($event)"
                      onPaste="return false"
                      :readonly="!editCurrentPanel"
                      type="text"
                      v-model="form.CANDIDATE_LIMIT_PRSNSRCH"
                      aria-label="Potential Candidate Result Limit - Search By Person"
                    >
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </es-form>
    </div>
    <div
      v-if="editCurrentPanel"
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 5.15rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click.prevent="cancelPanel()"
        style="margin-right: 1rem"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        @click.prevent="updatePanel()"
      >
        Update
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsForm,
    EsHeader,
  },
  props: {
    editOtherPanels: {
      type: Boolean,
    },
  },
  data() {
    return this.initialData();
  },
  created() {
    this.initialize(this.properties);
    this.allowedYearsOptions = [
      {
        description: 'Current Year Only',
      },
      {
        description: 'Current Year With Prior Year Overlap',
      },
      {
        description: 'All Years',
      },
    ];
    this.dataValidationHandlerOptions = [
      {
        description: 'Cancel All Errors',
      },
      {
        description: 'Fix Errors Online',
      },
    ];
    this.enabledDisabled = [
      {
        code: 'true',
        description: 'Enabled',
      },
      {
        code: 'false',
        description: 'Disabled',
      },
    ];
  },
  mounted() {
    this.updateCache();
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
  },
  methods: {
    ...mapActions({
      initProperties: 'propertiesModule/init',
    }),
    showOptional() {
      if (this.cache.SCHOOL_YEARS_ALLOWED === 'Current Year With Prior Year Overlap') {
        this.optionalShown = false;
      } else {
        this.optionalShown = true;
      }
    },
    hideOptional() {
      if (this.form.SCHOOL_YEARS_ALLOWED === 'Current Year With Prior Year Overlap') {
        this.optionalShown = false;
      } else {
        this.optionalShown = true;
        this.msgPriorYearStart = null;
        this.errPriorYearStart = false;
        this.msgPriorYearEnd = null;
        this.errPriorYearEnd = false;
        this.form.PRIOR_YEAR_OVERLAP_END_DATE = null;
        this.form.PRIOR_YEAR_OVERLAP_START_DATE = null;
      }
    },
    cancelPanel() {
      this.undoForm();
      this.resetFormErrors();
      this.hideNotification();
      this.editCurrentPanel = false;
      this.$emit('allowEditOtherPanels');
    },
    checkForFormChanges() {
      return this.compareObjects(this.cache, this.form);
    },
    checkInput(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except digits
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      }
    },
    compareObjects(o, n) {
      return JSON.stringify(o) !== JSON.stringify(n);
    },
    editPanel() {
      this.editCurrentPanel = !this.editCurrentPanel;
      this.$emit('allowEditOtherPanels');
    },
    groupBySection(data, type) {
      return Object.entries(data)
        .filter(([key]) => key.includes(type))
        .map(([key, val]) => ({ [key.replace(type, '')]: val }));
    },
    initialData() {
      return {
        cache: {},
        form: {},
        editCurrentPanel: false,

        optionalShown: null,
        msgPriorYearStart: null,
        errPriorYearStart: false,
        msgPriorYearEnd: null,
        errPriorYearEnd: false,

        errBirthYear: false,
        errSchoolYearStartDate: false,
        errMaxRecPerSubmission: false,
        errAllowedCharactersNames: false,
        msgBirthYear: null,
        msgSchoolYearStartDate: null,
        msgMaxRecPerSubmission: null,
        msgAllowedCharactersNames: null,

        errMaxNumberErrorsAllowed: false,
        msgMaxNumberErrorsAllowed: null,
      };
    },
    initialize(data) {
      this.form = Object.assign({}, ...this.groupBySection(data, 'RULE-'));
    },
    prepForRequest() {
      const payload = [];
      let cached = null;
      let obj = null;
      obj = this.form;
      cached = this.cache;
      this.pushToPayLoad('RULE', payload, obj, cached);
      return payload;
    },
    pushToPayLoad(category, payload, obj, cached) {
      Object.entries(obj).forEach(([property, value]) => {
        if (value !== cached[property]) {
          payload.push({ category, property, value });
        }
      });
    },
    resetFormErrors() {
      this.errBirthYear = false;
      this.msgBirthYear = null;
      this.errSchoolYearStartDate = false;
      this.msgSchoolYearStartDate = null;
      this.errMaxRecPerSubmission = false;
      this.errAllowedCharactersNames = false;
      this.msgMaxRecPerSubmission = null;
      this.errPriorYearStart = false;
      this.msgPriorYearStart = null;
      this.errPriorYearEnd = false;
      this.msgPriorYearEnd = null;
      this.msgAllowedCharactersNames = null;
      this.errMaxNumberErrorsAllowed = false;
      this.msgMaxNumberErrorsAllowed = null;
    },
    undoForm() {
      this.form = JSON.parse(JSON.stringify(this.cache));
    },
    updateCache() {
      this.cache = JSON.parse(JSON.stringify(this.form));
    },
    formHasErrors() {
      return this.errSchoolYearStartDate
        || this.errAllowedCharactersNames
        || this.errMaxRecPerSubmission
        || this.errBirthYear
        || this.errPriorYearStart
        || this.errPriorYearEnd
        || this.errMaxNumberErrorsAllowed;
    },
    async updatePanel() {
      const formError = this.formHasErrors();
      if (formError) {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
        return;
      }
      const changes = this.checkForFormChanges();
      if (!changes) {
        this.editCurrentPanel = false;
        this.hideNotification();
        return;
      }
      try {
        await axios({
          method: 'put',
          url: '/properties',
          data: this.prepForRequest(),
        });
        this.updateCache();
        await this.initProperties();
        this.showNotification('success', ['The Rule Properties have been updated successfully.']);
        this.$emit('allowEditOtherPanels');
        this.editCurrentPanel = false;
      } catch (error) {
        this.$emit('allowEditOtherPanels');
        let apiMsg = 'There were some errors.';
        const results = error.response.data;
        if (results) {
          apiMsg += results.map(r => r.error).join(', ');
        }
        this.showNotification('danger', [apiMsg]);
      }
    },
    validateBirthYear(userInput) {
      const currentYear = new Date().getFullYear();

      if (!userInput) {
        this.errBirthYear = true;
        this.msgBirthYear = 'Birth Year is required.';
      } else if (userInput.length !== 4 || userInput > currentYear) {
        this.errBirthYear = true;
        this.msgBirthYear = 'Earliest Birth Date is invalid.';
      } else {
        this.errBirthYear = false;
        this.msgBirthYear = null;
      }
    },
    validateAllowedCharactersNames() {
      const userInput = this.form.ALLOWED_CHARACTERS;
      if (!userInput) {
        this.errAllowedCharactersNames = true;
        this.msgAllowedCharactersNames = 'Allowed Characters (Names) is required';
      } else {
        this.errAllowedCharactersNames = false;
        this.msgAllowedCharactersNames = null;
      }
    },
    validateMaxRecPerSubmission() {
      const userInput = this.form.MAX_RECORDS_PER_SUB;
      if (!userInput) {
        this.errMaxRecPerSubmission = true;
        this.msgMaxRecPerSubmission = 'Maximum Records Per Submission is required';
      } else if (Number.isNaN(Number(userInput)) || userInput <= 0) {
        this.errMaxRecPerSubmission = true;
        this.msgMaxRecPerSubmission = 'Maximum Records Per Submission must be a positive number';
      } else {
        this.errMaxRecPerSubmission = false;
        this.msgMaxRecPerSubmission = null;
      }
    },
    validatePriorYearStart(userInput) {
      const startDate = this.form.YEAR_START_MONTH_DAY;
      const priorStartDate = this.form.PRIOR_YEAR_OVERLAP_START_DATE;
      const [startDateMonth, startDateDay] = this.form.YEAR_START_MONTH_DAY.split('/');
      const [priorStartDateMonth, priorStartDateDay] = this.form.PRIOR_YEAR_OVERLAP_START_DATE.split('/');
      const regexMonthDay = (/^\d{0,2}(\/\d{1,2})?$/g);
      if (regexMonthDay.test(userInput)) {
        const parsedDate = userInput.split('/');
        const month = parsedDate[0];
        const day = parsedDate[1];
        if ((priorStartDate === startDate)
          || (priorStartDateMonth < startDateMonth)
          || (priorStartDateDay < startDateDay)) {
          this.errPriorYearStart = true;
          this.msgPriorYearStart = 'Prior Year Overlap Period Start Date must be after the Year Start Date';
        } else if ((month === startDateMonth) && (day <= startDateDay)) {
          this.errPriorYearStart = true;
          this.msgPriorYearStart = 'Prior Year Overlap Period Start Date must be after the Year Start Date';
        } else {
          this.errPriorYearStart = false;
          this.msgPriorYearStart = null;
        }
      }
    },
    validatePriorYearEnd(userInput) {
      const endDate = this.form.YEAR_START_MONTH_DAY;
      const priorEndDate = this.form.PRIOR_YEAR_OVERLAP_END_DATE;
      const [endDateMonth, endDateDay] = this.form.PRIOR_YEAR_OVERLAP_START_DATE.split('/');
      const regexMonthDay = (/^\d{0,2}(\/\d{1,2})?$/g);
      if (regexMonthDay.test(userInput)) {
        const parsedDate = userInput.split('/');
        const month = parsedDate[0];
        const day = parsedDate[1];
        if ((month < endDateMonth) || (priorEndDate === endDate)) {
          this.errPriorYearEnd = true;
          this.msgPriorYearEnd = 'Prior Year Overlap Period Start Date must be before the Prior Year Overlap End Date';
        } else if ((month === endDateMonth) && (day <= endDateDay)) {
          this.errPriorYearEnd = true;
          this.msgPriorYearEnd = 'Prior Year Overlap Period Start Date must be before the Prior Year Overlap End Date';
        } else {
          this.errPriorYearEnd = false;
          this.msgPriorYearEnd = null;
        }
      }
    },
    validateMonthDay(userInput) {
      const regexMonthDay = (/^\d{0,2}(\/\d{1,2})?$/g);
      if (regexMonthDay.test(userInput)) {
        const year = new Date().getFullYear();
        const parsedDate = userInput.split('/');
        const month = parsedDate[0];
        const day = parsedDate[1];
        if ((month > 0) && (month < 13)) {
          this.errSchoolYearStartDate = false;
          this.msgSchoolYearStartDate = null;
          switch (month) {
            case '02':
              if ((year % 4 === 0 && year % 100) || year % 400 === 0) {
                if ((day > 0) && (day < 30)) {
                  this.errSchoolYearStartDate = false;
                  this.msgSchoolYearStartDate = null;
                } else {
                  this.errSchoolYearStartDate = true;
                  this.msgSchoolYearStartDate = 'Day is Invalid.';
                }
              } else if ((day > 0) && (day < 29)) {
                this.errSchoolYearStartDate = false;
                this.msgSchoolYearStartDate = null;
              } else {
                this.errSchoolYearStartDate = true;
                this.msgSchoolYearStartDate = 'Day is Invalid.';
              } return;
            case '01': case '03': case '05': case '07': case '08': case '10': case '12':
              if ((day > 0) && (day < 32)) {
                this.errSchoolYearStartDate = false;
                this.msgSchoolYearStartDate = null;
              } else {
                this.errSchoolYearStartDate = true;
                this.msgSchoolYearStartDate = 'Day is Invalid.';
              } return;
            case '04': case '06': case '09': case '11':
              if ((day > 0) && (day < 31)) {
                this.errSchoolYearStartDate = false;
                this.msgSchoolYearStartDate = null;
              } else {
                this.errSchoolYearStartDate = true;
                this.msgSchoolYearStartDate = 'Day is Invalid.';
              } return;
            default:
              this.errSchoolYearStartDate = true;
              this.msgSchoolYearStartDate = 'Month is invalid.';
          }
        } else {
          this.errSchoolYearStartDate = true;
          this.msgSchoolYearStartDate = 'Year Start Date is required.';
        }
      } else {
        this.errSchoolYearStartDate = true;
        this.msgSchoolYearStartDate = 'Year Start Date is required.';
      }
    },
    validateMaxNumberErrorsAllowed() {
      const userInput = this.form.MAX_ERRORS_ALLOWED;

      if (!userInput) {
        this.errMaxNumberErrorsAllowed = true;
        this.msgMaxNumberErrorsAllowed = 'Maximum Number of Errors Allowed is required.';
      } else if (Number.isNaN(Number(userInput)) || userInput.length > 9) {
        this.errMaxNumberErrorsAllowed = true;
        this.msgMaxNumberErrorsAllowed = 'Maximum Number of Errors Allowed must not exceed 9 digits.';
      } else {
        this.errMaxNumberErrorsAllowed = false;
        this.msgMaxNumberErrorsAllowed = null;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table td
    vertical-align: middle
    &:first-child
      border: 0
      width: 30px
  table th
    vertical-align: middle
  a.disabled
    color: #999
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  .property
    padding-left: 0
    font-size: 12px
    width: 70%
  .top-row
    padding-top: 1rem
  .header-container
    padding-left: 3rem
  .no-border
    border: 0
  .buttons
    float: right
    flex-wrap: nowrap
  .big-button
    width: 120px
    height: 78px
    white-space: normal
    word-wrap: break-word
  .input
    width: unset
</style>
